import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import type { Instrument } from '../lib/types';
import { Button, Card, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Select, Textarea } from '../components/ui';

/** INT-ALR-01: my own reminders — one-off or repeating, optionally about a symbol, trade or account. */

type Repeat = 'NONE' | 'DAILY' | 'WEEKDAYS' | 'WEEKLY';
interface ReminderView {
  id: string;
  title: string;
  note: string | null;
  repeat: Repeat;
  timezone: string;
  firstAt: string;
  nextAt: string | null;
  status: 'ACTIVE' | 'SNOOZED' | 'DONE' | 'CANCELLED';
  linked: string | null;
  href: string;
  instrumentId: string | null;
  tradeId: string | null;
  accountId: string | null;
}
interface TradeLite {
  id: string;
  direction: string;
  symbol: string;
  account: { code: string };
}

const REPEAT_LABEL: Record<Repeat, string> = { NONE: 'Once', DAILY: 'Every day', WEEKDAYS: 'Weekdays', WEEKLY: 'Every week' };

/** "now + 1 hour" rounded to the next 5 minutes, as local datetime-local value in `tz`. */
function defaultAt(tz: string) {
  const d = new Date(Date.now() + 60 * 60_000);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d);
  const p = (t: string) => parts.find((x) => x.type === t)?.value ?? '00';
  return `${p('year')}-${p('month')}-${p('day')}T${p('hour')}:${p('minute')}`;
}

export function Reminders() {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const list = useQuery({ queryKey: ['reminders'], queryFn: () => api<ReminderView[]>('/reminders'), refetchInterval: 30_000 });
  const instruments = useQuery({ queryKey: ['instruments', 'all'], queryFn: () => api<Instrument[]>('/instruments') });
  const trades = useQuery({ queryKey: ['trades', 'OPEN', 'mine'], queryFn: () => api<TradeLite[]>('/trades?status=OPEN&scope=mine') });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api<{ id: string; code: string }[]>('/accounts') });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['reminders'] });

  const blank = { title: '', note: '', at: defaultAt(tz), repeat: 'NONE' as Repeat, about: '' };
  const [f, setF] = useState(blank);
  const [editing, setEditing] = useState<string | null>(null);
  // The user's timezone arrives after the first render; move an untouched default time into it.
  useEffect(() => {
    if (!editing) setF((x) => (x.title ? x : { ...x, at: defaultAt(tz) }));
  }, [tz, editing]);
  const body = () => {
    const [kind, id] = f.about.split(':');
    return {
      title: f.title,
      note: f.note || undefined,
      at: f.at,
      timezone: tz,
      repeat: f.repeat,
      instrumentId: kind === 'symbol' ? id : editing ? '' : undefined,
      tradeId: kind === 'trade' ? id : editing ? '' : undefined,
      accountId: kind === 'account' ? id : editing ? '' : undefined,
    };
  };
  const save = useMutation({
    mutationFn: () => (editing ? api(`/reminders/${editing}`, { method: 'PATCH', body: body() }) : api('/reminders', { body: body() })),
    onSuccess: () => {
      refresh();
      setEditing(null);
      setF({ ...blank, at: defaultAt(tz) });
    },
  });
  const act = useMutation({ mutationFn: ({ id, path, body }: { id: string; path: string; body?: object }) => api(`/reminders/${id}/${path}`, { body: body ?? {} }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api(`/reminders/${id}`, { method: 'DELETE' }), onSuccess: refresh });

  const startEdit = (r: ReminderView) => {
    const local = new Intl.DateTimeFormat('en-CA', { timeZone: r.timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(r.nextAt ?? r.firstAt));
    const p = (t: string) => local.find((x) => x.type === t)?.value ?? '00';
    setEditing(r.id);
    setF({ title: r.title, note: r.note ?? '', at: `${p('year')}-${p('month')}-${p('day')}T${p('hour')}:${p('minute')}`, repeat: r.repeat, about: r.instrumentId ? `symbol:${r.instrumentId}` : r.tradeId ? `trade:${r.tradeId}` : r.accountId ? `account:${r.accountId}` : '' });
  };

  const upcoming = (list.data ?? []).filter((r) => r.status === 'ACTIVE' || r.status === 'SNOOZED');
  const done = (list.data ?? []).filter((r) => r.status === 'DONE');

  return (
    <>
      <PageHeader title="Reminders" sub={`Your own reminders, at the time you choose (${tz}). They reach you like other alerts — set channels in Settings → Notifications.`} />
      <div className="grid items-start gap-3.5 xl:grid-cols-[3fr_2fr]">
        <Card>
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="m-0 text-[15px] font-semibold">Upcoming · {upcoming.length}</h2>
          </div>
          <ErrorText error={list.error ? errorText(list.error) : act.error ? errorText(act.error) : null} />
          {list.isSuccess && upcoming.length === 0 && <Empty>No reminders yet. Add one on the right — e.g. “Check GBPUSD at London open”.</Empty>}
          {upcoming.map((r) => (
            <div key={r.id} className="flex flex-col gap-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-semibold">{r.title}</span>
                  <span className="text-xs text-sub">
                    {r.nextAt ? fmtTime(r.nextAt, tz, true) : '—'} · {REPEAT_LABEL[r.repeat]}
                    {r.linked && <> · <Link to={r.href}>{r.linked}</Link></>}
                  </span>
                  {r.note && <span className="text-sm whitespace-pre-line text-ink2">{r.note}</span>}
                </div>
                {r.status === 'SNOOZED' && <Pill tone="warn">snoozed</Pill>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[5, 15, 60].map((m) => (
                  <Button key={m} onClick={() => act.mutate({ id: r.id, path: 'snooze', body: { minutes: m } })} disabled={act.isPending}>
                    Snooze {m < 60 ? `${m} min` : '1 h'}
                  </Button>
                ))}
                {r.status === 'SNOOZED' && <Button onClick={() => act.mutate({ id: r.id, path: 'dismiss' })}>Dismiss</Button>}
                <Button onClick={() => startEdit(r)}>Edit</Button>
                <Button variant="danger" onClick={() => remove.mutate(r.id)} disabled={remove.isPending}>Delete</Button>
              </div>
            </div>
          ))}
          {done.length > 0 && (
            <>
              <h3 className="m-0 border-t border-line px-4 pt-3 pb-1 text-xs font-semibold tracking-[0.06em] text-muted uppercase">Done in the last 7 days</h3>
              {done.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm opacity-70">
                  <span>{r.title}{r.linked && <span className="text-xs text-muted"> · {r.linked}</span>}</span>
                  <div className="flex gap-1.5">
                    <Button onClick={() => act.mutate({ id: r.id, path: 'snooze', body: { minutes: 15 } })}>Remind me in 15 min</Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </Card>

        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">{editing ? 'Edit reminder' : 'New reminder'}</h2>
          <Field label="Remind me to"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Check GBPUSD at London open" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="When" hint={`Your time (${tz})`}><Input type="datetime-local" value={f.at} onChange={(e) => setF({ ...f, at: e.target.value })} /></Field>
            <Field label="Repeat">
              <Select value={f.repeat} onChange={(e) => setF({ ...f, repeat: e.target.value as Repeat })}>
                {(Object.keys(REPEAT_LABEL) as Repeat[]).map((k) => <option key={k} value={k}>{REPEAT_LABEL[k]}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="About (optional)">
            <Select value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })}>
              <option value="">Nothing in particular</option>
              {trades.data?.length ? (
                <optgroup label="My open trades">
                  {trades.data.map((t) => <option key={t.id} value={`trade:${t.id}`}>{t.symbol} {t.direction.toLowerCase()} · {t.account.code}</option>)}
                </optgroup>
              ) : null}
              {accounts.data?.length ? (
                <optgroup label="My accounts">
                  {accounts.data.map((a) => <option key={a.id} value={`account:${a.id}`}>{a.code}</option>)}
                </optgroup>
              ) : null}
              <optgroup label="Symbols">
                {instruments.data?.map((i) => <option key={i.id} value={`symbol:${i.id}`}>{i.symbol}</option>)}
              </optgroup>
            </Select>
          </Field>
          <Field label="Note (optional)"><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="What to look for" /></Field>
          <ErrorText error={save.error ? errorText(save.error) : null} />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending || !f.title.trim()}>{editing ? 'Save changes' : 'Add reminder'}</Button>
            {editing && <Button onClick={() => { setEditing(null); setF({ ...blank, at: defaultAt(tz) }); }}>Cancel</Button>}
          </div>
          <p className={cx('m-0 text-xs text-muted')}>Reminders arrive within a minute of the time you set. From the alert, open this page to snooze for 5, 15 or 60 minutes.</p>
        </Card>
      </div>
    </>
  );
}
